import { useEffect, useRef, useState } from 'react';

import { RuteroVertical, VoucherTextLanguage } from '@bandido/shared/types';

import { fetchVoucherPreview } from './queries';
import {
  VoucherPreviewBody,
  VoucherPreviewResult,
  voucherPreviewBody,
  voucherPreviewCanRetry,
  voucherPreviewKey,
  voucherPreviewSelection,
  voucherPreviewShouldRequest,
  voucherPreviewSkipReason,
  voucherPreviewView,
} from './voucherPreview';
import {
  VOUCHER_NO_REJECTION,
  VoucherTextRejection,
  VoucherTextsDraft,
  voucherRejectionEmpty,
  voucherTextsServerIssues,
} from './voucherTexts';

/** Lo que se espera de quietud antes de pedir el dibujo. */
export const VOUCHER_PREVIEW_DEBOUNCE_MS = 500;

/**
 * Cuánto vive la URL que se abrió en otra pestaña. El mismo minuto que usa la
 * descarga del documento de una reserva (`VoucherAction.tsx`).
 */
export const VOUCHER_PREVIEW_OPEN_TTL_MS = 60_000;

/**
 * LA VISTA PREVIA en vivo: el reloj, el pedido y el ciclo de vida del blob.
 * QUÉ se muestra lo decide `voucherPreviewView`, que se prueba sin DOM.
 *
 * Tres cosas que no son opcionales, porque esto se dispara con cada tecla:
 *
 * 1. **El pedido es UNO solo por identidad.** El cuerpo y su clave se congelan
 *    juntos cuando el reloj vence, así que la respuesta se juzga contra el
 *    cuerpo que se mandó: comparando contra la clave del render, un cambio de
 *    vertical mandaba el pedido, lo descartaba por "viejo" y lo repetía 500 ms
 *    después — dos PDFs renderizados en el servidor para dibujar uno.
 * 2. **Se descarta la respuesta que ya no corresponde.** Escribiendo rápido, la
 *    que tarda más vuelve última y dejaba en pantalla un documento anterior.
 * 3. **Cada blob se libera.** Un `createObjectURL` por tecla sin `revoke` es un
 *    PDF vivo en memoria por tecla.
 */
export function useVoucherPreview(params: {
  agencyId: number;
  loaded: boolean;
  texts: VoucherTextsDraft;
  /** Los problemas que la pantalla ya detectó: con eso no se pide nada. */
  issues: Record<string, unknown>;
  vertical: RuteroVertical;
  /** El proveedor de la fila abierta, si hay una. */
  provider?: string;
  lang: VoucherTextLanguage;
  /**
   * La identidad GUARDADA con la que el endpoint dibuja: no viaja en el
   * cuerpo, pero si cambia hay que volver a pedir (`voucherIdentityStamp`).
   */
  identityStamp: string;
  /**
   * El 400 del preview, para mostrarlo en la fila que lo causó. Viaja entero
   * —con `otros`— porque un rechazo que esta pantalla no puede ubicar en una
   * fila tiene que prender el cartel general en vez de perderse.
   */
  onServerIssues: (rejection: VoucherTextRejection) => void;
}): {
  url?: string;
  loading: boolean;
  messageKey: string | null;
  alternativeKey?: string;
  /** Abre el documento en otra pestaña, con una URL que no se va a revocar. */
  open: () => void;
  /** Volver a pedir el MISMO borrador, cuando el fallo fue pasajero. */
  retry?: () => void;
} {
  const { agencyId, loaded, texts, issues, vertical, provider, lang, identityStamp } = params;
  const body = voucherPreviewBody({ agencyId, vertical, provider, lang, texts });
  const key = voucherPreviewKey(body, identityStamp);
  const hasIssues = Object.keys(issues).length > 0;

  /** El cuerpo congelado que se pide, con SU clave: los dos siempre juntos. */
  const [pedido, setPedido] = useState<{ body: VoucherPreviewBody; key: string }>({ body, key });
  const [url, setUrl] = useState<string | undefined>(undefined);
  const [inFlight, setInFlight] = useState(false);
  const [result, setResult] = useState<VoucherPreviewResult | { status: 'error' } | undefined>(
    undefined,
  );
  /** La selección del dibujo que está en pantalla. */
  const [drawnFor, setDrawnFor] = useState<string | undefined>(undefined);

  /** Los verticales que ya dijeron que no tienen documento. */
  const sinDocumento = useRef<string[]>([]);
  /** La clave que la pantalla espera AHORA. */
  const esperando = useRef(key);
  esperando.current = key;
  /** La clave que está viajando y TODAVÍA se puede aceptar. */
  const enCurso = useRef<string | undefined>(undefined);
  /** La última clave cuya respuesta se llegó a APLICAR en pantalla. */
  const aplicada = useRef<string | undefined>(undefined);
  /** Sube cuando el asesor pide reintentar: es lo que vuelve a correr el efecto. */
  const [intento, setIntento] = useState(0);
  /** El blob que se está mostrando, para poder liberarlo. */
  const mostrando = useRef<string | undefined>(undefined);
  /** El documento en sí, para poder abrirlo aparte del visor. */
  const documento = useRef<Blob | undefined>(undefined);
  /** Cuántos pedidos hay viajando: la rueda gira si y solo si hay alguno. */
  const enVuelo = useRef(0);
  const avisarIssues = useRef(params.onServerIssues);
  avisarIssues.current = params.onServerIssues;
  /** El borrador de ahora: las cajas de proveedor donde cae un 400 salen de él. */
  const borrador = useRef(texts);
  borrador.current = texts;

  // El pedido no sigue a cada tecla: espera a que el asesor pare de escribir.
  // Se congelan cuerpo y clave JUNTOS, del mismo render.
  useEffect(() => {
    const timer = setTimeout(() => setPedido({ body, key }), VOUCHER_PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // La dependencia es la CLAVE: `body` es el del render que la produjo, y
    // agregarlo repetiría el efecto en cada render —justo lo que el reloj
    // evita— porque es un objeto nuevo cada vez.
  }, [key]);

  useEffect(() => {
    const motivo = voucherPreviewSkipReason({
      loaded,
      hasIssues,
      unsupported: sinDocumento.current,
      vertical: pedido.body.vertical,
    });
    // Nada que pedir. La rueda refleja lo que hay en vuelo, no este momento.
    if (motivo) return;
    /*
     * El pedido congelado YA quedó viejo y el reloj está por traer el nuevo:
     * mandarlo sería un PDF que se descarta seguro. Pasaba en cada apertura de
     * la pantalla —el efecto corre al llegar la configuración, con el borrador
     * todavía vacío— y en cada cambio de vertical: dos renders del documento en
     * el servidor para dibujar uno.
     */
    if (pedido.key !== esperando.current) return;
    // Ya hay una respuesta que va a contestar esto: una viajando que se puede
    // aceptar, o una ya aplicada. Una que se DESCARTÓ no cuenta.
    if (
      !voucherPreviewShouldRequest({
        key: pedido.key,
        inFlight: enCurso.current,
        applied: aplicada.current,
      })
    ) {
      return;
    }
    enCurso.current = pedido.key;

    let vigente = true;
    enVuelo.current += 1;
    setInFlight(true);

    /** Cambia lo que se muestra, liberando lo anterior. */
    const reemplazar = (
      nueva: string | undefined,
      para: string | undefined,
      blob: Blob | undefined,
    ) => {
      if (mostrando.current) URL.revokeObjectURL(mostrando.current);
      mostrando.current = nueva;
      documento.current = blob;
      setUrl(nueva);
      setDrawnFor(para);
    };

    fetchVoucherPreview(pedido.body)
      .then((respuesta) => {
        // Se dibuja solo si la pantalla sigue esperando ESTE pedido.
        if (!vigente || esperando.current !== pedido.key) return;
        aplicada.current = pedido.key;
        if (respuesta.status === 'unsupported' && !sinDocumento.current.includes(pedido.body.vertical)) {
          sinDocumento.current = [...sinDocumento.current, pedido.body.vertical];
        }
        setResult(respuesta);
        avisarIssues.current(VOUCHER_NO_REJECTION);
        reemplazar(
          respuesta.status === 'ready' ? URL.createObjectURL(respuesta.blob) : undefined,
          respuesta.status === 'ready' ? voucherPreviewSelection(pedido.body) : undefined,
          respuesta.status === 'ready' ? respuesta.blob : undefined,
        );
      })
      .catch((error) => {
        if (!vigente || esperando.current !== pedido.key) return;
        const leido = voucherTextsServerIssues(error, borrador.current);
        avisarIssues.current(leido);
        if (!voucherRejectionEmpty(leido)) {
          // Un 400 por un bloque SÍ es un resultado aplicado: ya se muestra en
          // su fila, y el mismo cuerpo va a volver a fallar igual.
          aplicada.current = pedido.key;
          return;
        }
        /*
         * Fallo pasajero (red caída, 500). NO cuenta como aplicado: si contara,
         * este borrador quedaba marcado como "ya contestado" para siempre y la
         * vista previa no se recuperaba ni volviendo la conexión, salvo
         * cambiando el texto o recargando la página.
         */
        setResult({ status: 'error' });
      })
      .finally(() => {
        // La rueda se apaga aunque la respuesta se haya DESCARTADO: si no, un
        // pedido superado la dejaba girando para siempre. Por eso es un
        // contador y no un booleano por efecto: con dos pedidos vivos, el
        // primero en volver no puede apagar la rueda del otro.
        enVuelo.current = Math.max(0, enVuelo.current - 1);
        // Solo si sigue siendo ESTE el que viaja: un pedido posterior ya puede
        // haber tomado su lugar.
        if (enCurso.current === pedido.key) enCurso.current = undefined;
        setInFlight(enVuelo.current > 0);
      });

    return () => {
      vigente = false;
      /*
       * Este pedido dejó de poder aceptarse, así que deja de contar como "ya
       * pedido": si el mismo borrador vuelve a ser el actual —se escribió un
       * error y se deshizo—, hay que pedirlo de nuevo o la columna se queda
       * con el documento viejo para siempre.
       */
      if (enCurso.current === pedido.key) enCurso.current = undefined;
    };
  }, [pedido, loaded, hasIssues, intento]);

  // Al desmontar, el último blob también se libera.
  useEffect(
    () => () => {
      if (mostrando.current) URL.revokeObjectURL(mostrando.current);
    },
    [],
  );

  const vista = voucherPreviewView({
    skip: voucherPreviewSkipReason({
      loaded,
      hasIssues,
      unsupported: sinDocumento.current,
      vertical,
    }),
    inFlight,
    drawnFor,
    selection: voucherPreviewSelection(body),
    result,
    vertical,
  });
  return {
    url: vista.showDocument ? url : undefined,
    loading: vista.loading,
    messageKey: vista.messageKey,
    ...(vista.alternativeKey ? { alternativeKey: vista.alternativeKey } : {}),
    /*
     * La pestaña nueva recibe una URL PROPIA, no la del visor: la del visor se
     * revoca en cuanto llega el dibujo siguiente —medio segundo después de la
     * próxima tecla— y la pestaña que el asesor acababa de abrir se quedaba en
     * blanco. Se libera sola después, como hace la descarga de la reserva.
     */
    ...(voucherPreviewCanRetry(result)
      ? {
        retry: () => {
          // El mismo cuerpo otra vez: el borrador no se toca.
          enCurso.current = undefined;
          aplicada.current = undefined;
          setResult(undefined);
          setIntento((n) => n + 1);
        },
      }
      : {}),
    open: () => {
      if (!vista.showDocument || !documento.current) return;
      const aparte = URL.createObjectURL(documento.current);
      window.open(aparte, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(aparte), VOUCHER_PREVIEW_OPEN_TTL_MS);
    },
  };
}
